import _ from "underscore"; // Ensure underscore is used in the code to avoid unused import errors
import { MobileActions } from "../../store/mobile/actions";
import { updateAppCache } from "../../utils/appcache";
import serviceWorker, { TServiceWorkerStatus } from "../../utils/serviceWorker";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import pageHTML from "./offline.tmpl";
import DebugPageView from "./debugPageView";
import { CancelablePromise } from "../../utils/cancelablePromise";

type TAppCacheStatus = "uncached" | "idle" | "checking" | "downloading" | "updateready" | "obsolete" | "unknown" | "error" | "nocache";

const YELLOW = "#ffbf00";
const RED = "#ac3f3f";
const GREEN = "#3fac3f";
const BLUE = "#3f3fac";

export default class AppCacheView extends DebugPageView {
    private readonly template = _.template(pageHTML);
    private currentCacheStatus: TAppCacheStatus;
    private appCacheProgress = 0;
    private appCacheUpdate;
    private serviceWorkerUpdate;

    constructor (options: any) {
        super(options);
        this.template = _.template(pageHTML);
    }

    private get appCacheStatus(): TAppCacheStatus {
        if (!this.currentCacheStatus) {
            if (!window.applicationCache) {
                return "nocache";
            }

            let appCache = window.applicationCache;
            switch (appCache.status) {
                case appCache.UNCACHED: // UNCACHED == 0
                    return "uncached";
                case appCache.IDLE: // IDLE == 1
                    return "idle";
                case appCache.CHECKING: // CHECKING == 2
                    return "checking";
                case appCache.DOWNLOADING: // DOWNLOADING == 3
                    return "downloading";
                case appCache.UPDATEREADY: // UPDATEREADY == 4
                    return "updateready";
                case appCache.OBSOLETE: // OBSOLETE == 5
                    return "obsolete";
                default:
                    return "unknown";
            }
        }
        return this.currentCacheStatus;
    }

    private get serviceWorkerStatus(): TServiceWorkerStatus {
        return serviceWorker.state;
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events () {
        return {
            "click #updateAppCache": this.updateAppCache,
            "click #updateServiceWorker": this.updateServiceWorker
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    toggleAppCacheEvents (handlerFunc: EventListener, isAttach = true) { // Add type annotation for handlerFunc
        if (window.applicationCache) {
            if (isAttach) {
                window.applicationCache.addEventListener("noupdate", handlerFunc, false);
                window.applicationCache.addEventListener("cached", handlerFunc, false);
                window.applicationCache.addEventListener("updateready", handlerFunc, false);
                window.applicationCache.addEventListener("progress", handlerFunc, false);
                window.applicationCache.addEventListener("error", handlerFunc, false);
            } else {
                window.applicationCache.removeEventListener("noupdate", handlerFunc, false);
                window.applicationCache.removeEventListener("cached", handlerFunc, false);
                window.applicationCache.removeEventListener("updateready", handlerFunc, false);
                window.applicationCache.removeEventListener("progress", handlerFunc, false);
                window.applicationCache.removeEventListener("error", handlerFunc, false);
            }
        }
    }

    toggleServiceWorkerEvents(handlerFunc: (...args: any[]) => void, isAttach = true) { // Add type annotation for handlerFunc
        if (isAttach) {
            dispatcher.on(DispatcherEvents.ServiceWorkerInstallSuccess, handlerFunc, this);
            dispatcher.on(DispatcherEvents.ServiceWorkerRegistered, handlerFunc, this);
            dispatcher.on(DispatcherEvents.ServiceWorkerUpdateActivated, handlerFunc, this);
            dispatcher.on(DispatcherEvents.ServiceWorkerUpdateFound, handlerFunc, this);
            dispatcher.on(DispatcherEvents.ServiceWorkerUpdateInstalling, handlerFunc, this);
            dispatcher.on(DispatcherEvents.ServiceWorkerUpdateSuccess, handlerFunc, this);
        } else {
            dispatcher.off(DispatcherEvents.ServiceWorkerInstallSuccess, handlerFunc, this);
            dispatcher.off(DispatcherEvents.ServiceWorkerRegistered, handlerFunc, this);
            dispatcher.off(DispatcherEvents.ServiceWorkerUpdateActivated, handlerFunc, this);
            dispatcher.off(DispatcherEvents.ServiceWorkerUpdateFound, handlerFunc, this);
            dispatcher.off(DispatcherEvents.ServiceWorkerUpdateInstalling, handlerFunc, this);
            dispatcher.off(DispatcherEvents.ServiceWorkerUpdateSuccess, handlerFunc, this);
        }
    }

    getAppCacheStatusColour() {
        switch (this.appCacheStatus) {
            case "unknown":
                return YELLOW;
            case "error":
            case "nocache":
                return RED;
            case "checking":
            case "downloading":
                return BLUE;
            default:
                return GREEN;
        }
    }

    getServiceWorkerStatusColour() {
        switch (this.serviceWorkerStatus) {
            case "unknown":
                return YELLOW;
            case "redundant":
                return RED;
            case "installing":
            case "activating":
                return BLUE;
            default:
                return GREEN;
        }
    }

    render () {
        this.el.innerHTML = this.template({
            status: this.appCacheStatus,
            progress: `${ this.appCacheProgress }%`,
            appCacheStatusColour: this.getAppCacheStatusColour(),
            serviceWorkerStatus: this.serviceWorkerStatus,
            serviceWorkerStatusColour: this.getServiceWorkerStatusColour()
        });

        this.app.store.dispatch(MobileActions.setPageState({
            title: "Offline",
            pageLevel: 2
        }));

        if (!this.appCacheUpdate) {
            this.appCacheUpdate = e => {
                let evt = e.originalEvent ? e.originalEvent : e;
                this.currentCacheStatus = evt.type;
                if (evt.type === "progress") {
                    if (evt.loaded && evt.total) {
                        this.appCacheProgress = Math.floor(evt.loaded / evt.total * 100);
                    } else {
                        this.appCacheProgress = 0;
                    }
                }
                this.render();
            };

            this.toggleAppCacheEvents(this.appCacheUpdate);
        }

        if (!this.serviceWorkerUpdate) {
            this.serviceWorkerUpdate = () => {
                this.render();
            };
            this.toggleServiceWorkerEvents(this.serviceWorkerUpdate);
        }

        return this;
    }

    updateAppCache (): void {
        updateAppCache();
    }

    updateServiceWorker (): CancelablePromise<ServiceWorkerRegistration> {
        return serviceWorker.update();
    }

    remove (): this {
        if (this.appCacheUpdate) {
            this.toggleAppCacheEvents(this.appCacheUpdate, false);
        }
        if (this.serviceWorkerUpdate) {
            this.toggleAppCacheEvents(this.serviceWorkerUpdate, false);
        }
        super.remove();
        return this;
    }
}
